import axios, { AxiosError } from 'axios';
import { Sentry } from '../config/sentry';
import {
  TokenRefreshResponse,
  RequestConfig,
  NavigatorWithConnection,
  NetworkErrorDetails,
  ExtendedAxiosError,
  ApiErrorResponse
} from '../types/axiosConfig';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api/v1`;
const API_BASE_BACKUP_URL = `https://backup.${BACKEND_URL.split("/").pop()}/api/v1`;

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes timeout
});

// Create separate axios instance for refresh token requests (without interceptors)
const refreshAxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout for refresh requests
});

// Token refresh state
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];
let refreshErrorSubscribers: ((error: Error) => void)[] = [];

// Subscribe to token refresh
const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

// Subscribe to refresh errors
const subscribeRefreshError = (cb: (error: Error) => void) => {
  refreshErrorSubscribers.push(cb);
};

// Notify subscribers about new token
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
  refreshErrorSubscribers = [];
};

// Handle failed refresh
const onRefreshError = (error: Error) => {
  refreshErrorSubscribers.forEach((cb) => cb(error));
  refreshSubscribers = [];
  refreshErrorSubscribers = [];
};

// Clear authentication and redirect to login
const clearAuthAndRedirect = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  
  // Only redirect if we're not already on the login page
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
};

// Handle token refresh
const refreshToken = async (): Promise<string> => {
  const refreshTokenValue = localStorage.getItem('refreshToken');
  
  if (!refreshTokenValue || refreshTokenValue === 'undefined' || refreshTokenValue === 'null') {
    throw new Error('Token de atualização inválido');
  }

  try {
    // Use separate axios instance to avoid interceptor recursion
    const response = await refreshAxiosInstance.post<TokenRefreshResponse>(`${API_BASE_URL}/auth/refresh`, {
      refresh_token: refreshTokenValue
    });

    const { data } = response;
    if (!data?.data?.access_token || !data?.data?.refresh_token) {
      throw new Error('Formato de resposta de token inválido');
    }

    const { access_token, refresh_token } = data.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('refreshToken', refresh_token);

    return access_token;
  } catch (error) {
    // If refresh fails, clear tokens and prepare for redirect
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    
    // Re-throw the error to be handled by the calling code
    throw error;
  }
};


/**
 * Gets detailed information about a network error
 */
const getNetworkErrorDetails = (error: AxiosError): NetworkErrorDetails => {
  const config = error.config as RequestConfig;
  
  // Calculate request duration if we have the start time
  let requestDuration = null;
  if (config?.startTime) {
    requestDuration = Date.now() - config.startTime;
  }
  
  const navigatorWithConnection = navigator as NavigatorWithConnection;
  
  return {
    url: config?.url,
    baseURL: config?.baseURL,
    method: config?.method,
    headers: config?.headers ? JSON.stringify(config.headers) : null,
    timeoutConfig: config?.timeout,
    requestDuration,
    timestamp: new Date().toISOString(),
    errorMessage: error.message,
    errorCode: error.code,
    errorName: error.name,
    networkInfo: {
      online: navigator.onLine,
      userAgent: navigator.userAgent,
      connectionType: navigatorWithConnection.connection?.effectiveType || null,
      connectionDownlink: navigatorWithConnection.connection?.downlink || null,
      connectionRtt: navigatorWithConnection.connection?.rtt || null,
    }
  };
};

// Request interceptor to add auth token to requests and record request start time
axiosInstance.interceptors.request.use(
  (config) => {
    // Track when the request started for timing information
    (config as RequestConfig).startTime = Date.now();
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh and retry with backup URL
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RequestConfig;

    // If the request already retried with the backup URL, reject the error
    if (originalRequest?.baseURL === API_BASE_BACKUP_URL) {
      const errorDetails = getNetworkErrorDetails(error);
      const errorCode = errorDetails.errorCode;

      // Create a more descriptive error with details
      const frontendError = new Error(`Network error: ${errorCode}`);
      frontendError.name = `FrontendNetworkError_${errorCode}`;

      // Create user-friendly error message based on error type
      const userMessage = 'Não foi possível conectar ao servidor. Por favor, tente novamente daqui a alguns instantes.';

      // Assign user message to the error
      (error as ExtendedAxiosError).userMessage = userMessage;

      // Report to Sentry with detailed context
      Sentry.captureException(frontendError, {
        tags: {
          errorType: 'frontend_network',
          networkErrorCode: errorCode,
          component: 'axios_interceptor',
          endpoint: errorDetails.url || 'unknown'
        },
        extra: errorDetails
      });
      return Promise.reject(error);
    }

    // If the request to API_BASE_URL fails WITH A NETWORK ERROR (no response),
    // retry with API_BASE_BACKUP_URL
    // Only retry for network errors, not for response errors like 500
    // Skip backup retry in development (localhost)
    const isLocalhost = API_BASE_URL.includes('localhost') || API_BASE_URL.includes('127.0.0.1');
    if (originalRequest?.baseURL === API_BASE_URL && !error.response && !isLocalhost) {
      originalRequest.baseURL = API_BASE_BACKUP_URL;
      try {
        return await axiosInstance(originalRequest);
      } catch (backupError) {
        return Promise.reject(backupError);
      }
    }

    // Handle other errors (e.g., 401 token refresh logic)
    // Skip token refresh for auth endpoints - let them handle their own 401 errors
    const isAuthRequest = originalRequest.url?.includes('/auth/login') || 
                         originalRequest.url?.includes('/auth/register') ||
                         originalRequest.url?.includes('/auth/verify-email') ||
                         originalRequest.url?.includes('/auth/refresh'); // Add refresh to excluded auth endpoints
    
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      if (isRefreshing) {
        try {
          const token = await new Promise<string>((resolve, reject) => {
            subscribeTokenRefresh(resolve);
            subscribeRefreshError(reject);
          });
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // If refresh failed, clear auth and redirect
          clearAuthAndRedirect();
          return Promise.reject(refreshError);
        }
      }

      isRefreshing = true;

      try {
        const newToken = await refreshToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        onTokenRefreshed(newToken);
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Clear auth and redirect on refresh failure
        clearAuthAndRedirect();
        onRefreshError(refreshError instanceof Error ? refreshError : new Error('Token refresh failed'));
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;

/**
 * Extracts an error message from an API error response
 * Provides consistent error handling across all service functions
 * 
 * @param error - The error object from an API call
 * @param fallbackMessage - Default message to use if no detail is found
 * @returns {string} User-friendly error message
 */
export const extractErrorMessage = (error: AxiosError | Error, fallbackMessage: string): string => {
  // Check if this is a network error (no response from server)
  if (!('response' in error) || !error.response) {
    // Log to Sentry if not already captured by interceptor
    if (error.message && error.message !== 'Network Error') {
      Sentry.captureException(error, {
        tags: { errorType: 'frontend_api' }
      });
    }
    
    // Return a user-friendly message for network errors
    return error.message === 'Network Error' 
      ? 'Não foi possível conectar ao servidor. Por favor, tente novamente daqui a alguns instantes.'
      : `${fallbackMessage}. Por favor, tente novamente daqui a alguns instantes.`;
  }
  
  // Check for specific error response format
  if (error.response?.data) {
    const errorData = error.response.data as ApiErrorResponse;
    
    // If there's a detail field, use it
    if (typeof errorData.detail === 'string') {
      return errorData.detail;
    }
    
    // Some endpoints may return an error_message field
    if (typeof errorData.error_message === 'string') {
      return errorData.error_message;
    }
    
    // Some endpoints may return a message field
    if (typeof errorData.message === 'string') {
      return errorData.message;
    }
  }
  
  // Use default message if no specific error was found
  return `${fallbackMessage}. Por favor, tente novamente daqui a alguns instantes.`;
};